import { gameSlug, slugify } from './gameSlug.js';

/**
 * Where the in-app "back" out of a game leads when the game was opened from somewhere other than the
 * Games list — today, a portfolio's dashboard. The sidebar's back row (and the onboarding wizard's
 * exit) read it instead of going to /game.
 *
 * Remembered per tab in sessionStorage, so a refresh inside the game keeps it, and dropped as soon as
 * the user is anywhere outside that game (clearGameReturnIfLeft, run on every navigation) — back at
 * the portfolio, on the Games list, in another game. Opening the game any other way therefore leaves
 * the normal back target in place.
 */
const KEY = 'gp_game_return';

const read = () => {
    try { return JSON.parse(sessionStorage.getItem(KEY) || 'null'); } catch (_) { return null; }
};

// Whether `pathname` is inside the remembered game: its pages (/game/<slug>/…) or its setup wizard
// (/game/new/<id>/…). Matched by the id the link ends in, so a rename doesn't count as leaving; old name-only links count too.
const isInsideGame = (entry, pathname) => {
    if (!entry || !pathname) return false;
    const [, root, first, second] = pathname.split('/');
    if (root !== 'game' || !first) return false;
    if (first === 'new') return second === entry.gameId;
    return first === entry.gameId || first.endsWith(`-${entry.gameId}`)
        || first === entry.slug || (!!entry.legacySlug && first === entry.legacySlug);
};

/** Remember that `game` was opened from `path` (shown as `label` on the back row). */
export const setGameReturn = ({ game, path, label }) => {
    if (!game?.id || !path) return;
    try {
        sessionStorage.setItem(KEY, JSON.stringify({
            gameId: game.id, slug: gameSlug(game), legacySlug: slugify(game.gameName), path, label,
        }));
    } catch (_) { /* storage unavailable — back falls through to the Games list */ }
};

/** The return target for the game `pathname` is inside, or null. */
export const gameReturnFor = (pathname) => {
    const entry = read();
    return isInsideGame(entry, pathname) ? entry : null;
};

/** Forget the return target once `pathname` is outside its game. */
export const clearGameReturnIfLeft = (pathname) => {
    const entry = read();
    if (entry && !isInsideGame(entry, pathname)) {
        try { sessionStorage.removeItem(KEY); } catch (_) { /* nothing to clear */ }
    }
};
